"use client";

import { usePathname } from "@/i18n/routing";
import { ProductDetails } from "@/lib/centra/formatters";
import { useSearchParams } from "next/navigation";
import React, { createContext, useContext, useMemo } from "react";

type ProductContext = {
  activeVariantName?: string;
  setActiveVariant: (variantId: string | undefined) => void;
  activeVariant?: ProductDetails;
  product: ProductDetails;
};

export const VARIANT_QUERY_KEY = "variant";
const ProductContext = createContext<ProductContext>(null!);

type ProductProviderProps = {
  initialActiveVariant?: string;
  productData: ProductDetails;
  children: React.ReactNode;
};

export const ProductProvider = ({
  children,
  initialActiveVariant,
  productData,
}: ProductProviderProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const activeVariantName =
    searchParams.get(VARIANT_QUERY_KEY) || initialActiveVariant;

  const activeVariant = useMemo(() => {
    return (
      productData?.variants?.find(
        item => item.variantName === activeVariantName,
      ) ?? productData?.variants?.[0]
    );
  }, [productData, activeVariantName]) as ProductDetails;

  const setActiveVariant = (variantId: string | undefined) => {
    const variant = productData?.variants?.find(
      item => item.itemId === variantId,
    );
    if (!variant || !variantId) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set(VARIANT_QUERY_KEY, variant.variantName);

    window.history.replaceState({}, "", `${pathname}?${newParams.toString()}`);
  };

  return (
    <ProductContext.Provider
      value={{
        activeVariantName,
        setActiveVariant,
        activeVariant,
        product: productData,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProductContext = () => useContext(ProductContext);

export function getProductLink({
  locale,
  slug,
  variantId,
}: {
  locale: string;
  slug: string;
  variantId?: string;
}) {
  const base = `/${locale}/product/${slug}`;
  if (variantId) {
    return `${base}?${VARIANT_QUERY_KEY}=${variantId}`;
  }
  return base;
}
